/**
 * @file Video Dropoff Calculator Constants
 * @description Constants and static data for the video dropoff calculator
 */

// FAQ items for the calculator
export const FAQ_ITEMS = [
  {
    question: "What is video retention and drop-off?",
    answer: "Video retention measures the percentage of viewers who stay watching at each point in your video, while drop-off measures the percentage who leave at each segment. Retention is cumulative (always decreasing or flat), while drop-off rates can fluctuate and help identify specific problematic segments."
  },
  {
    question: "How do I interpret the retention curve?",
    answer: "The retention curve shows what percentage of your initial viewers remain at each point in the video. A steep decline indicates viewers leaving quickly, while a gradual slope shows better audience retention. Look for sudden drops, which might indicate content issues at those specific points."
  },
  {
    question: "What's the difference between retention and drop-off rates?",
    answer: "Retention rate is calculated relative to your initial audience (total views), showing the percentage of original viewers still watching at each point - it's cumulative and always decreases. Drop-off rate is calculated relative to the previous breakpoint, showing the percentage of viewers who left during that specific segment. Both metrics provide different and valuable insights into viewer behavior: retention shows overall engagement strength, while drop-off helps identify specific problem areas in your content."
  },
  {
    question: "How should I interpret the normalized drop-off rate?",
    answer: "The normalized drop-off rate (shown in %/sec) adjusts for the time duration between breakpoints, making it easier to compare drop-off intensity across different segments. A higher rate indicates more viewers are leaving per second in that segment. This is particularly useful when comparing segments of different lengths or identifying the most critical moments where you're losing audience attention."
  },
  {
    question: "How accurate are these calculations?",
    answer: "The calculations are based on the data you provide from platform analytics. The accuracy depends on the granularity and completeness of the view data from platforms like Facebook, TikTok, YouTube, or Vimeo. Each platform has different measurement standards—Facebook counts 3-second views, TikTok measures 2-second views and 6-second views, while YouTube and Vimeo report percentage-based retention—which affects how retention metrics should be interpreted across platforms."
  },
  {
    question: "What are good video advertising retention rate benchmarks?",
    answer: "Industry benchmarks vary by platform: Facebook ads typically see 15-25% thumbstop rates (users stopping to watch) with 10-15% watching to 25% completion. TikTok averages 15-20% completion rates for 15-second ads, while YouTube pre-roll ads retain 65-70% of viewers through the first 5 seconds with 25-40% completion rates for skippable ads. Branded content generally performs 10-15% better than direct response ads in retention metrics. Keep in mind that these benchmarks are highly dependent on additional factors such as ad length, format, and platform algorithm preferences."
  },
  {
    question: "What are good retention benchmarks for advertising videos on my platform?",
    answer: "Advertising video retention varies by platform, ad format, and targeting. Generally: Facebook/Instagram ads should achieve 20-30% thumbstop rates with 10-20% completion for longer ads. TikTok ads should maintain 70%+ retention at 6 seconds with 30-50% completion rates. YouTube pre-roll ads typically see 65-75% retention at 5 seconds with 25-40% completion for skippable formats. Performance varies significantly based on creative quality, audience targeting, and campaign objectives - direct response ads often see lower retention than brand awareness campaigns."
  },
  {
    question: "Should I focus on fixing high drop-off points or improving overall retention?",
    answer: "Both are important, but start with the largest drop-off points first - these represent the biggest opportunities for improvement. Use the 'Largest Post-Hook Drop' feature to identify your most critical problem area. However, don't ignore overall retention patterns. Sometimes multiple small improvements across the video can have a bigger impact than fixing one large drop-off point."
  },
  {
    question: "How can I improve my video retention?",
    answer: "To improve retention: 1) Create a strong hook in the first 3-5 seconds, 2) Place your most engaging content early, 3) Keep videos concise (optimal length varies by platform), 4) Use pattern interrupts (scene changes, graphics) every 15-20 seconds, 5) Tell a compelling story with clear structure, and 6) Include a strong call-to-action. Analyze your drop-off points to identify specific content that may need improvement."
  }
] as const

// Platform Breakpoint Generation Constants
export const PLATFORM_CONSTANTS = {
  FACEBOOK: {
    INITIAL_DROP_RETENTION: 0.20, // 80% drop-off -> 20% retention at 3s
    THRUPLAY_RETENTION: 0.10, // Approx 10% reach ThruPlay
  },
  TIKTOK: {
    INITIAL_DROP_RETENTION_2S: 0.25, // 75% drop -> 25% retention at 2s
    INITIAL_DROP_RETENTION_6S: 0.15, // 85% drop -> 15% retention at 6s
  },
  YOUTUBE: {
    BASE_RETENTION_25: 0.15,
    BASE_RETENTION_50: 0.10,
    BASE_RETENTION_75: 0.06,
    BASE_RETENTION_100: 0.04,
  },
  VIMEO: {
    // Vimeo is an embedded/on-site hosting platform, NOT a paid-social feed, so
    // its viewing is far higher-intent than the FB/TikTok ad curves above — these
    // retention estimates are grounded in published business/embedded-video data
    // rather than ad drop-off:
    //   • Wistia, State of Video Report — videos under 1 min average ~52% of the
    //     video watched (engagement/avg-% -watched), ~47% under 5 min, falling for
    //     longer runtimes. https://wistia.com/learn/marketing/video-marketing-statistics
    //   • Vimeo Help Center — "completion rate" = views played within ~2s of the
    //     end; the retention graph is the % of views still present at each point.
    //     https://help.vimeo.com/hc/en-us/articles/21137121658897-About-viewer-retention-stats
    //   • Industry/business-video completion commonly cited at ~45–75% ("good"
    //     50–70%), well above paid-social completion.
    // The quartile retentions below are chosen so the implied average % watched
    // (area under the curve) lands near Wistia's ~52% for a short embedded video,
    // with a conservative ~35% completion default. They are STARTING estimates a
    // user overwrites with their own Vimeo analytics; the length factor in
    // platform-breakpoints.ts scales them (shorter videos retain better).
    BASE_RETENTION_25: 0.70,
    BASE_RETENTION_50: 0.55,
    BASE_RETENTION_75: 0.42,
    BASE_RETENTION_100: 0.35,
  },
  BASE_RETENTION: {
    RETENTION_25: 0.10,
    RETENTION_50: 0.07,
    RETENTION_75: 0.05,
    RETENTION_100: 0.03,
  }
} as const
